import React, { useEffect } from 'react';
import type { AppProps } from 'next/app';
import Script from 'next/script';
import { DefaultSeo } from 'next-seo';
import { useRouter } from 'next/router';
import { UiProvider } from 'context/ui';
import { Analytics as VercelAnalytics } from '@vercel/analytics/react';
import AnalyticsProvider, { BlockKlaviyo } from '@/analytics/AnalyticsProvider';
import { PostHogProvider } from '@/analytics/PostHogProvider';
import {
    BUILD_ENVIRONMENT,
    CODE_BRANCH,
    CODE_VERSION,
    GOOGLE_TAG_MANAGER_ID,
    HEAP_ANALYTICS,
    IS_PRODUCTION,
    LOGROCKET_ID,
    MUZOLOGY_API,
    ROLLBAR_TOKEN,
    WEBSITE_URL
} from '@/lib/defaults';
import 'theme/styles.scss';
import 'theme/global.scss';
import 'theme/fonts/monotype.css';
import axios from 'axios';
// import LogRocket from 'logrocket';
// import { GoogleTagManager } from '@next/third-parties/google';
// import { Inter } from '@next/font/google';
// const inter = Inter({ subsets: ['latin'] });
axios.defaults.baseURL = MUZOLOGY_API;

// eslint-disable-next-line camelcase
import { Inter, Roboto, Poppins, Lexend_Deca, Gloria_Hallelujah, Schoolbell } from 'next/font/google';

/* eslint-disable new-cap */
const inter = Inter({ subsets: ['latin'], weight: ['300', '400', '500', '600', '700', '800', '900'], variable: '--font-inter' });
const roboto = Roboto({ subsets: ['latin'], weight: ['400', '700'], variable: '--font-roboto' });
const poppins = Poppins({ subsets: ['latin'], weight: ['100', '200', '300', '400', '500', '600', '700', '800', '900'], variable: '--font-poppins' });
const lexendDeca = Lexend_Deca({ subsets: ['latin'], weight: ['100', '200', '300', '400', '500', '600', '700', '800', '900'], variable: '--font-lexend-deca' });
const gloriaHallelujah = Gloria_Hallelujah({ subsets: ['latin'], weight: ['400'], variable: '--font-gloria-hallelujah' });
const schoolBell = Schoolbell({ subsets: ['latin'], weight: ['400'], variable: '--font-schoolbell' });
/* eslint-enable new-cap */

function MyApp({ Component, pageProps }: AppProps) {
    const router = useRouter();

    // Track route changes with Rudderstack
    useEffect(() => {
        const handleRouteChange = (url: string) => {
            console.log('route change:', url);

            // rudderstack / audience 11
            if (window.rudderanalytics) {
                console.log('[Rudderstack] page = ', url);
                // @ts-ignore
                if (window.rudderanalytics.page) {
                    // @ts-ignore
                    window.rudderanalytics.page(url, url, null, BlockKlaviyo);
                }
                // @ts-ignore
                window.rudderanalytics.page(url);
            }
        };

        // subscribe to route changes
        router.events.on('routeChangeComplete', handleRouteChange);

        // unsubscribe from route changes on component unmount
        return () => router.events.off('routeChangeComplete', handleRouteChange);
    }, [router.events]);

    return (
        <PostHogProvider>
            <DefaultSeo
                openGraph={{
                    type: 'website',
                    locale: 'en_IE',
                    siteName: 'Muzology',
                }}
                twitter={{
                    handle: '@MuzologyEdu',
                    site: '@MuzologyEdu',
                    cardType: 'summary_large_image'
                }}
            />

            {GOOGLE_TAG_MANAGER_ID && (
                <Script id='gtm' strategy='afterInteractive'>
                    {`
                        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                        })(window,document,'script','dataLayer','${GOOGLE_TAG_MANAGER_ID}');
                    `}
                </Script>
            )}

            {/*
            {HEAP_ANALYTICS && <Script id='heap'>{`
                    window.heap=window.heap||[],heap.load=function(e,t){window.heap.appid=e,window.heap.config=t=t||{};var r=document.createElement("script");r.type="text/javascript",r.async=!0,r.src="https://cdn.heapanalytics.com/js/heap-"+e+".js";var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(r,a);for(var n=function(e){return function(){heap.push([e].concat(Array.prototype.slice.call(arguments,0)))}},p=["addEventProperties","addUserProperties","clearEventProperties","identify","resetIdentity","removeEventProperty","setEventProperties","track","unsetEventProperty"],o=0;o<p.length;o++)heap[p[o]]=n(p[o])};
                    heap.load("${HEAP_ANALYTICS}");
            `}</Script>}
            */}

            <div className={`${inter.variable} ${roboto.variable} ${poppins.variable} ${lexendDeca.variable} ${gloriaHallelujah.variable} ${schoolBell.variable}`}>
                <UiProvider>
                    <AnalyticsProvider pageProps={pageProps}>
                        <Component {...pageProps} />
                    </AnalyticsProvider>
                </UiProvider>
            </div>

            {/*<VercelAnalytics />*/}
        </PostHogProvider>
    );
}

export default MyApp;
